import { useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { ConsultationModal } from "@/components/ConsultationModal";
import { Reveal, itemAnim, heroAnim } from "@/components/Reveal";
import { findEstate } from "@/data/estates";

const copy = {
  en: {
    nav: ["Our approach", "Services", "Estates", "About"],
    back: "All estates",
    about: "About this estate",
    amenities: "Considered details",
    sampleNote: "Sample showcase — details and photography are illustrative until the official portfolio is announced.",
    nightly: "from",
    nightlySuffix: "per night · managed by Eden",
    ctaEyebrow: "OWN A VILLA LIKE THIS?",
    ctaTitle: <>Let’s make your<br /><em>villa remarkable.</em></>,
    ctaText: "This is what Eden care looks like. Tell us about your property — we’ll come back with a considered point of view.",
    ctaButton: "Start a conversation",
    footer: "Keeping Bali’s finest estates in their best light.",
    footerDist: "Distribution Channels",
    contact: "Talk to our team",
  },
  id: {
    nav: ["Pendekatan kami", "Layanan", "Properti", "Tentang"],
    back: "Semua properti",
    about: "Tentang properti ini",
    amenities: "Detail yang dipikirkan matang",
    sampleNote: "Presentasi contoh — detail dan foto bersifat ilustrasi hingga portofolio resmi diumumkan.",
    nightly: "mulai",
    nightlySuffix: "per malam · dikelola Eden",
    ctaEyebrow: "PUNYA VILLA SEPERTI INI?",
    ctaTitle: <>Mari buat villa Anda<br /><em>lebih berkesan.</em></>,
    ctaText: "Beginilah standar perawatan Eden. Ceritakan properti Anda — kami akan kembali dengan pandangan yang matang.",
    ctaButton: "Mulai percakapan",
    footer: "Menjaga villa terbaik Bali dalam cahaya terbaiknya.",
    footerDist: "Kanal Distribusi",
    contact: "Hubungi tim kami",
  },
};

export default function EstateDetail({ lang, setLang }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const estate = findEstate(slug);
  const t = copy[lang];

  if (!estate) return <Navigate to="/" replace />;

  return <div className="site-shell">
    <Nav lang={lang} setLang={setLang} onConsult={() => setModalOpen(true)} labels={t.nav} ctaLabel={t.contact} />

    <main id="top">
      <section className="estate-hero" data-testid="estate-detail-hero">
        <div className="hero-image" style={{ backgroundImage: `url(${estate.heroImage})` }} />
        <div className="hero-overlay" />
        <motion.div className="estate-hero-content" {...heroAnim(0.15)}>
          <button className="back-link" onClick={() => navigate("/")} data-testid="estate-back-button"><ArrowLeft size={15} /> {t.back}</button>
          <motion.p className="eyebrow light" {...heroAnim(0.35)}>{estate.location} · {estate.tag[lang]}</motion.p>
          <motion.h1 data-testid="estate-detail-title" {...heroAnim(0.5)}>{estate.name}</motion.h1>
        </motion.div>
      </section>

      <div className="spec-strip" data-testid="estate-specs">
        {estate.specs.map(([label, value], i) => (
          <motion.div className="spec" key={i} data-testid={`spec-${i}`} {...itemAnim(i)}>
            <span>{label[lang]}</span><b>{value}</b>
          </motion.div>
        ))}
      </div>

      <section className="estate-body" data-testid="estate-about">
        <Reveal>
          <p className="eyebrow">{t.about}</p>
          <p className="estate-description">{estate.description[lang]}</p>
          <p className="estate-note" data-testid="estate-sample-note">{t.sampleNote}</p>
          <p className="estate-nightly">{t.nightly} <strong>${estate.nightly}</strong> <small>{t.nightlySuffix}</small></p>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="eyebrow">{t.amenities}</p>
          <ul className="amenity-list" data-testid="estate-amenities">
            {estate.amenities[lang].map((item, i) => <li key={i} data-testid={`amenity-${i}`}><Check size={14} />{item}</li>)}
          </ul>
        </Reveal>
      </section>

      <section className="gallery-grid" data-testid="estate-gallery">
        {estate.gallery.map((g, i) => (
          <motion.figure key={i} className={i === 0 ? "g-main" : "g-sub"} data-testid={`gallery-image-${i}`} {...itemAnim(i)}>
            <div className="gallery-photo" style={{ backgroundImage: `url(${g.src})` }} />
            <figcaption>{g.caption[lang]}</figcaption>
          </motion.figure>
        ))}
      </section>

      <section className="closing section-dark estate-cta" data-testid="estate-cta">
        <Reveal className="closing-content">
          <p className="eyebrow gold">{t.ctaEyebrow}</p>
          <h2>{t.ctaTitle}</h2>
          <p>{t.ctaText}</p>
          <button className="button button-light" onClick={() => setModalOpen(true)} data-testid="estate-cta-button">{t.ctaButton} <ArrowUpRight size={17} /></button>
        </Reveal>
        <div className="closing-orb" />
      </section>
    </main>

    <Footer footerText={t.footer} contactLabel={t.contact} distLabel={t.footerDist} onConsult={() => setModalOpen(true)} />
    <ConsultationModal lang={lang} open={modalOpen} onClose={() => setModalOpen(false)} ctaLabel={t.ctaButton} />
  </div>;
}
